export type PublicReadinessState = "ready_now" | "preview" | "not_ready";

export interface PublicReadinessRow {
  capability: string;
  ignition81: PublicReadinessState;
  ignition83: PublicReadinessState;
  limitation: string;
}

/**
 * Customer-safe projection of the canonical roadmap and verification matrix.
 *
 * Policy:
 * - ready_now: a current retained customer-path receipt exists for both the
 *   named capability and the selected Ignition line;
 * - preview: implementation exists, but commissioning, live proof, or a
 *   workflow dependency remains open;
 * - not_ready: there is no complete customer workflow to offer yet.
 */
export const PUBLIC_READINESS_VERIFIED_AT = "2026-07-24";

export const PUBLIC_READINESS_ROWS: PublicReadinessRow[] = [
  {
    capability: "Per-developer sandbox provisioning",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "Project and Gateway commissioning varies by project.",
  },
  {
    capability: "Native Vision Client testing",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "An attached client and fresh acceptance evidence are required.",
  },
  {
    capability: "Discovery audit (project health)",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "Fresh end-to-end proof remains open on both supported lines.",
  },
  {
    capability: "Unattended cross-version suite execution",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "Fresh commissioned stopped-to-cleanup acceptance remains open on both supported lines.",
  },
  {
    capability: "Gherkin draft and approval promotion",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "Authoring exists; current discovery-to-execution proof is incomplete.",
  },
  {
    capability: "MCP catalog and local transports",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "The core catalog is implemented, but the current customer runtime path is not closed.",
  },
  {
    capability: "PocketFlow trace and approval guardrails",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "Core guardrails are implemented; current unattended customer-path proof remains open.",
  },
  {
    capability: "Environment version save and rollback (GitHub)",
    ignition81: "ready_now",
    ignition83: "ready_now",
    limitation: "Customer-owned GitHub repositories support environment creation, saved versions, and same-environment recovery on both supported lines.",
  },
  {
    capability: "AG OPC value and namespace readback",
    ignition81: "ready_now",
    ignition83: "ready_now",
    limitation: "Bounded data-plane proof across Tags.json, Native tag-export, and Schneider sources; not Vision or datasource proof.",
  },
  {
    capability: "AG scenario-pack approve and execute",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "Approval and a fail-closed Execute boundary are proven on both lines; full executed/connected scenario closure depends on Native/Runtime Orchestrator tag-read validation, which remains open.",
  },
  {
    capability: "Ignition datasource and historian validation",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "Support resources can be provisioned, but Gateway-side connections require validation.",
  },
  {
    capability: "AI-assisted test drafting",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "Drafting is evidence-gated and does not replace commissioned runtime proof.",
  },
  {
    capability: "Provider-linked GitHub writes",
    ignition81: "ready_now",
    ignition83: "ready_now",
    limitation: "OrbitCell validates the customer account, repository, and branch before linking the environment and saving its initial version.",
  },
  {
    capability: "Commissioned seed restore and apply",
    ignition81: "not_ready",
    ignition83: "not_ready",
    limitation: "No complete customer restore/apply workflow is currently offered.",
  },
  {
    capability: "GitLab and Gitea provider support",
    ignition81: "not_ready",
    ignition83: "not_ready",
    limitation: "Planned after the GitHub provider workflow.",
  },
  {
    capability: "CI/CD with signed deployment and rollback",
    ignition81: "not_ready",
    ignition83: "not_ready",
    limitation: "Roadmap capability; not available for customer use.",
  },
  {
    capability: "Field simulation support resources",
    ignition81: "preview",
    ignition83: "preview",
    limitation: "Resources may be connectable; Ignition-side connection proof is still required.",
  },
];

export function combinedReadiness(
  row: PublicReadinessRow,
): "Ready now" | "Preview" | "Not ready" {
  if (row.ignition81 === "ready_now" && row.ignition83 === "ready_now") {
    return "Ready now";
  }
  if (row.ignition81 === "not_ready" && row.ignition83 === "not_ready") {
    return "Not ready";
  }
  return "Preview";
}
